import React from 'react';
import { useTranslation } from 'react-i18next';
import { aboutContent } from '../data/aboutContent';

const About = () => {
  const { t, i18n } = useTranslation();
  const content = aboutContent[i18n.language] || aboutContent.en;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hemp-bg-gradient py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center p-8 bg-gradient-to-br from-white to-hemp-green-50 rounded-2xl shadow-2xl">
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight drop-shadow-lg">
              {content.title}
            </h1>
            <p className="text-xl text-gray-800 max-w-3xl mx-auto font-medium bg-white/80 px-4 py-3 rounded-lg">
              {content.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Purpose Section */}
          <div className="mb-16">
            <div className="hemp-card">
              <h2 className="text-2xl font-bold text-hemp-green-800 mb-4">
                {content.purpose.title}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {content.purpose.content}
              </p>
            </div>
          </div>

          {/* Scope Section */}
          <div className="mb-16">
            <div className="hemp-card">
              <h2 className="text-2xl font-bold text-hemp-green-800 mb-4">
                {content.scope.title}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {content.scope.content}
              </p>
            </div>
          </div>

          {/* Responsibilities Section */}
          <div className="mb-16">
            <div className="hemp-card">
              <h2 className="text-2xl font-bold text-hemp-green-800 mb-4">
                {content.responsibilities.title}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {content.responsibilities.content}
              </p>
              
              {/* Key Roles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="bg-hemp-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-hemp-green-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('about.roles.farmManager')}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t('about.roles.farmManagerDesc')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-hemp-brown-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-hemp-brown-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('about.roles.safetyOfficer')}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t('about.roles.safetyOfficerDesc')}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="bg-hemp-leaf-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-hemp-leaf-700" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('about.roles.fieldTechnicians')}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {t('about.roles.fieldTechniciansDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Core Procedures Section */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-hemp-green-800 mb-4">
                {content.coreProcedures.title}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {content.coreProcedures.items.map((item, index) => (
                <div key={index} className="hemp-card">
                  <div className="flex items-center mb-4">
                    <div className="bg-hemp-green-100 rounded-lg w-12 h-12 flex items-center justify-center mr-4">
                      <span className="text-hemp-green-700 font-bold text-lg">{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
